import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type SpotQuote } from '../domain/SpotQuote'
import { type SpotQuoteType } from '../domain/SpotQuoteType'
import type { SubmitSpotQuoteParams } from '../infra/SpotQuoteMapper'
import { bookNowSpotQuote } from '../use-cases/bookNowSpotQuote'
import { getSpotQuote } from '../use-cases/getSpotQuote'
import { pollBookNowStatus } from '../use-cases/pollBookNowStatus'
import { rejectSpotQuote } from '../use-cases/rejectSpotQuote'
import { submitSpotQuote } from '../use-cases/submitSpotQuote'

type State = {
  spotQuote?: SpotQuote
  spotQuoteStatus: RequestStatus
}

const initialState = {
  spotQuoteStatus: RequestStatus.Initial,
}

export function useSpotQuote() {
  const [{ spotQuote, spotQuoteStatus }, setSpotQuoteState] = useState<State>(initialState)
  const [submitSpotQuoteStatus, setSubmitSpotQuoteStatus] = useState<RequestStatus>(
    RequestStatus.Initial
  )
  const [rejectSpotQuoteStatus, setRejectSpotQuoteStatus] = useState<RequestStatus>(
    RequestStatus.Initial
  )
  const [bookNowSpotQuoteStatus, setBookNowSpotQuoteStatus] = useState<RequestStatus>(
    RequestStatus.Initial
  )

  const fetchSpotQuote = useCallback(async (id: string, { signal }: AbortController) => {
    try {
      setSpotQuoteState(previous => ({ ...previous, spotQuoteStatus: RequestStatus.Pending }))

      const spotQuoteResult = await getSpotQuote(id, { signal })

      setSpotQuoteState(previous => ({
        ...previous,
        spotQuote: spotQuoteResult,
        spotQuoteStatus: RequestStatus.Done,
      }))
    } catch (error) {
      if (!signal.aborted) {
        setSpotQuoteState(previous => ({
          ...previous,
          spotQuoteStatus: RequestStatus.Error,
        }))
      }
    }
  }, [])

  const submitQuote = useCallback(async (draftQuote: SubmitSpotQuoteParams) => {
    setSubmitSpotQuoteStatus(RequestStatus.Pending)

    try {
      await submitSpotQuote(draftQuote)

      setSubmitSpotQuoteStatus(RequestStatus.Done)
    } catch (error) {
      setSubmitSpotQuoteStatus(RequestStatus.Error)

      throw error
    }
  }, [])

  const rejectQuote = useCallback(async (id: string, reason: string) => {
    setRejectSpotQuoteStatus(RequestStatus.Pending)

    try {
      await rejectSpotQuote(id, reason)

      setRejectSpotQuoteStatus(RequestStatus.Done)
    } catch (error) {
      setRejectSpotQuoteStatus(RequestStatus.Error)
      throw error
    }
  }, [])

  const bookNowQuote = useCallback(
    async (draftQuote: SubmitSpotQuoteParams, quoteType: SpotQuoteType) => {
      setBookNowSpotQuoteStatus(RequestStatus.Pending)

      try {
        //BookNowAction uuid
        const { uuid } = await bookNowSpotQuote(draftQuote)
        //poll to update book now status
        const { shipmentLink } = await pollBookNowStatus(uuid, draftQuote.id)
        if (quoteType === 'single' && shipmentLink) {
          window.location.href = shipmentLink
        }
        setBookNowSpotQuoteStatus(RequestStatus.Done)
      } catch (error) {
        setBookNowSpotQuoteStatus(RequestStatus.Error)
      }
    },
    []
  )

  return {
    spotQuote,
    spotQuoteStatus,
    submitSpotQuoteStatus,
    bookNowSpotQuoteStatus,
    rejectSpotQuoteStatus,
    fetchSpotQuote,
    submitQuote,
    bookNowQuote,
    rejectQuote,
  }
}
